import React, { useEffect, useState } from "react";
import { Loader2, Star, Sparkles, Building2, Flame, Users, GraduationCap } from "lucide-react";
import api from "../../lib/api";
import DiscoverLayout from "../../components/discover/DiscoverLayout";
import StartupCard from "../../components/discover/StartupCard";
import { motion } from "framer-motion";

const FEATURED_COLLECTIONS = [
  { id: "ai", label: "Top AI Startups", icon: Sparkles, desc: "Breakout machine learning models, conversational automation, and LLM platforms." },
  { id: "saas", label: "Top SaaS Companies", icon: Building2, desc: "Software as a Service products solving critical business problems." },
  { id: "women", label: "Women Founders", desc: "Startups led by outstanding female entrepreneurs and executives.", icon: Users },
  { id: "student", label: "Student Startups", desc: "Ventures incubated in campuses and built by student builders.", icon: GraduationCap },
  { id: "fastest", label: "Fastest Growing", desc: "Breakout metrics showing accelerated Month-on-Month expansion.", icon: Flame },
  { id: "unicorns", label: "Newest Unicorns", desc: "Startups valued at $1 Billion or more in recent funding announcements.", icon: Star },
];

export default function FeaturedListsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedList, setSelectedList] = useState("ai");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/discover/startups?limit=100")
      .then((r) => {
        if (alive) {
          // Add some mock tags to help filter collections nicely
          const raw = r.data.items || [];
          const enhanced = raw.map((c, index) => {
            const extraTags = [];
            if (index % 4 === 0) extraTags.push("Women Founders");
            if (index % 5 === 0) extraTags.push("Student Founded");
            return {
              ...c,
              tags: [...(c.tags || []), ...extraTags],
            };
          });
          setItems(enhanced);
        }
      })
      .catch((e) => console.error("Error loading featured lists:", e))
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  const getFilteredItems = () => {
    switch (selectedList) {
      case "ai":
        return items.filter((s) => s.industry === "AI/ML" || (s.tags && s.tags.includes("AI Startups")));
      case "saas":
        return items.filter((s) => s.industry === "SaaS" || s.industry === "Enterprise");
      case "women":
        return items.filter((s) => s.tags && s.tags.includes("Women Founders"));
      case "student":
        return items.filter((s) => s.tags && s.tags.includes("Student Founded"));
      case "fastest":
        return items.filter((s) => s.tags && (s.tags.includes("Fast Growing") || s.tags.includes("Trending")));
      case "unicorns":
        return items.filter((s) => (s.stage && s.stage.toLowerCase().includes("unicorn")) || (s.tags && s.tags.includes("Unicorn")));
      default:
        return items;
    }
  };

  const filteredItems = getFilteredItems();
  const currentCollection = FEATURED_COLLECTIONS.find((c) => c.id === selectedList) || FEATURED_COLLECTIONS[0];

  return (
    <DiscoverLayout
      title="Featured Lists"
      subtitle="Hand-picked curated lists showcasing technology leaders, campus innovators, and female-led rocketships in the ecosystem."
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left selector sidebar (30%) */}
        <div className="lg:col-span-4 space-y-3.5">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1 mb-2">
            Curated Collections
          </p>
          <div className="flex flex-col gap-2">
            {FEATURED_COLLECTIONS.map((col) => {
              const Icon = col.icon;
              const isSelected = selectedList === col.id;

              return (
                <button
                  key={col.id}
                  onClick={() => setSelectedList(col.id)}
                  className={`w-full text-left p-4 rounded-2xl border transition duration-200 flex items-start gap-3.5 ${
                    isSelected
                      ? "bg-white border-[#7C3AED] shadow-sm ring-1 ring-[#7C3AED]/5"
                      : "bg-white border-gray-150 hover:border-gray-300 hover:shadow-sm"
                  }`}
                >
                  <div
                    className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                      isSelected
                        ? "bg-[#7C3AED] text-white"
                        : "bg-indigo-50 text-[#7C3AED] border border-indigo-100"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <h4
                      className={`text-xs font-bold transition-colors ${
                        isSelected ? "text-[#7C3AED]" : "text-gray-900"
                      }`}
                    >
                      {col.label}
                    </h4>
                    <p className="text-[10px] text-gray-400 mt-1 leading-relaxed font-normal line-clamp-1">
                      {col.desc}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right showcase section (70%) */}
        <div className="lg:col-span-8 space-y-6">
          <div className="bg-white border border-gray-150 rounded-[24px] p-6 shadow-sm">
            <h3 className="font-display font-bold text-gray-950 text-lg leading-snug">
              {currentCollection.label}
            </h3>
            <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
              {currentCollection.desc}
            </p>
          </div>

          {loading ? (
            <div className="py-20 flex justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#7C3AED]" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-16 rounded-2xl border border-dashed border-gray-200 bg-white shadow-sm">
              <p className="text-sm text-gray-500 font-medium">No startups match this collection.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fadeIn">
              {filteredItems.map((c) => (
                <StartupCard key={c.slug} c={c} />
              ))}
            </div>
          )}
        </div>
      </div>
    </DiscoverLayout>
  );
}
